// src/pages/Home.js

import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    FiArrowRight, FiBookOpen, FiDownloadCloud, FiTrendingUp, FiCalendar,
    FiEye, FiFlag, FiShield, FiCpu, FiUsers, FiAward, FiBarChart2, FiGlobe,
    FiMessageSquare, FiBriefcase, FiZap, FiCheckCircle, FiHeart, FiStar, FiThumbsUp
} from 'react-icons/fi';
import SectionTitle from '../components/SectionTitle';
import logoImg from '../assets/images/logo.png';
import portadaImg from '../assets/images/portada.webp';
import '../styles/Home.css';

import { db } from '../firebase-config';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';

import confeclisaLogo from '../assets/images/Organismos/confeclisa-logo.png';
import cenasLogo from '../assets/images/Organismos/cenas-logo.png';
import imgFLH from '../assets/images/Organismos/flh.jpg';
import imgFIH from '../assets/images/Organismos/fih.jpg';
import imgOMS from '../assets/images/Organismos/oms.png';
import imgOPS from '../assets/images/Organismos/ops.jpg';
import imgOISS from '../assets/images/Organismos/oiss.jpg';
import imgISG from '../assets/images/Organismos/isg.png';

// --- DATOS PARA SECCIONES ---
const resourcesData = [
  {
    title: 'Estudios de Mercado',
    description: 'Accede a informes y análisis exclusivos sobre tendencias y proyecciones del sector.',
    icon: <FiTrendingUp />,
    link: '/estudios-de-mercado',
  },
  {
    title: 'Biblioteca de Documentos',
    description: 'Consulta nuestro repositorio de documentos legales, guías y publicaciones especializadas.',
    icon: <FiBookOpen />,
    link: '/biblioteca',
  },

];

const servicesData = [
  {
    icon: <FiBriefcase />,
    title: 'Consultoría Estratégica',
    description: 'Análisis profundo y soluciones a medida para optimizar la gestión en el sector salud.'
  },
  {
    icon: <FiZap />,
    title: 'Innovación Tecnológica',
    description: 'Impulsamos la adopción de tecnologías de vanguardia para modernizar su modelo de negocio.'
  },
  {
    icon: <FiShield />,
    title: 'Asesoramiento Legal',
    description: 'Expertos en el marco regulatorio para garantizar el cumplimiento y proteger sus intereses.'
  },
];

const strategicAxes = [
    { icon: <FiTrendingUp />, title: 'Crecimiento Institucional', description: 'Ampliar y diversificar nuestra base de socios, fomentando la participación activa.' },
    { icon: <FiGlobe />, title: 'Federalización', description: 'Asegurar una presencia y representación real en todas las regiones del país.' },
    { icon: <FiCpu />, title: 'Modernización Digital', description: 'Desarrollar un portal de servicios de referencia y un campus virtual para la formación.' },
    { icon: <FiAward />, title: 'Formación y Asistencia', description: 'Implementar un programa permanente de capacitación y asistencia técnica para instituciones.' },
    { icon: <FiCalendar />, title: 'Congreso Internacional', description: 'Posicionar nuestro congreso anual como el foro nacional de referencia en políticas de salud.' },
    { icon: <FiBarChart2 />, title: 'Producción de Conocimiento', description: 'Establecer un observatorio sectorial que genere informes y publicaciones estratégicas.' },
    { icon: <FiUsers />, title: 'Inteligencia de Mercado', description: 'Brindar herramientas de gestión como un observatorio de precios y compras conjuntas.' }
];

const valuesData = [
    { icon: <FiGlobe />, title: 'Federalismo' },
    { icon: <FiCheckCircle />, title: 'Calidad' },
    { icon: <FiStar />, title: 'Innovación' },
    { icon: <FiEye />, title: 'Transparencia' },
    { icon: <FiHeart />, title: 'Colaboración' }
];


// --- VARIANTES DE ANIMACIÓN ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2, ease: "easeOut", duration: 0.6 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 80, damping: 18 },
  },
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 70,
      damping: 18,
      delay: 0.2
    }
  },
};

const AnimatedSeparator = () => (
  <div className="section-separator">
    <div className="line left" />
    <div className="diamond" />
    <div className="line right" />
  </div>
);


// --- SECCIÓN "PLAN ESTRATÉGICO" ---
const StrategicPlanSection = () => (
  <motion.section
    className="strategic-plan-section"
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
    variants={containerVariants}
  >
    <div className="container">
      <motion.div className="sp-intro" variants={itemVariants}>
        <h2 className="sp-title">Un Futuro en Construcción: Plan Estratégico 2025-2028</h2>
        <p className="sp-subtitle">
          Iniciamos una nueva etapa para consolidarnos como el referente nacional del sector salud privado, recuperando nuestra voz para la defensa, promoción y modernización del sector.
        </p>
      </motion.div>

      <motion.div className="sp-main-layout" variants={containerVariants}>
        <motion.div className="sp-left-column" variants={itemVariants}>
          <motion.div className="sp-vision-mission-box" variants={itemVariants}>
            <h4><FiEye /> Visión</h4>
            <p>Ser la cámara empresaria de salud más representativa y federal de la Argentina, reconocida por su capacidad de articular actores, generar conocimiento y promover innovación para el desarrollo del sector.</p>
          </motion.div>
          <motion.div className="sp-vision-mission-box" variants={itemVariants}>
            <h4><FiShield /> Misión</h4>
            <p>Integrar y representar a las empresas de salud del país, acompañándolas con herramientas de gestión, formación y análisis, y elevando propuestas que fortalezcan la sustentabilidad del sistema sanitario argentino.</p>
          </motion.div>
          <motion.div className="sp-values-box" variants={itemVariants}>
             <h4><FiStar/> Nuestros Valores</h4>
             <div className="sp-values-grid">
                {valuesData.map((value, index) => (
                    <motion.div key={index} className="sp-value-item" variants={itemVariants}>
                        {value.icon}
                        <span>{value.title}</span>
                    </motion.div>
                ))}
             </div>
          </motion.div>
        </motion.div>

        <motion.div className="sp-right-column" variants={containerVariants}>
            <h3 className="sp-axes-title">Ejes Estratégicos</h3>
            <div className="sp-axes-grid">
                {strategicAxes.map((axis, index) => (
                    <motion.div key={index} className="sp-axis-item" variants={itemVariants}>
                        <div className="sp-axis-icon">{axis.icon}</div>
                        <div className="sp-axis-text">
                            <h4>{axis.title}</h4>
                            <p>{axis.description}</p>
                        </div>
                    </motion.div>
                ))}
            </div>
        </motion.div>
      </motion.div>
    </div>
  </motion.section>
);


const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  const [latestNews, setLatestNews] = useState([]);

  const internationalOrganizations = [
    { nombre: 'Federación Latinoamericana de Hospitales (FLH)', logo: imgFLH, url: 'https://www.flh-fhl.org/' },
    { nombre: 'Federación Internacional de Hospitales (FIH)', logo: imgFIH, url: 'https://ihf-fih.org/' },
    { nombre: 'Organización Mundial de la Salud (OMS)', logo: imgOMS, url: 'https://www.who.int/es' },
    { nombre: 'Organización Panamericana de la Salud (OPS)', logo: imgOPS, url: 'https://www.paho.org/es' },
    { nombre: 'Organización Iberoamericana de Seguridad Social (OISS)', logo: imgOISS, url: 'https://oiss.org/' },
    { nombre: 'Instituto de salud publica y gestion sanitaria (ISG)', logo: imgISG, url: 'https://isg.org.ar/' },
  ];

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const newsCollectionRef = collection(db, 'noticias');
        const q = query(newsCollectionRef, orderBy('timestamp', 'desc'), limit(2));
        const data = await getDocs(q);
        setLatestNews(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      } catch (error) {
        console.error("Error al obtener las últimas noticias:", error);
      }
    };
    fetchNews();
  }, []);

  return (
    <div className="home-container">

      {/* SECCIÓN HERO */}
      <section className="hero-section" ref={heroRef}>
        <motion.div
          className="hero-background-image"
          style={{ backgroundImage: `url(${portadaImg})` , y: parallaxY }}
        ></motion.div>
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <motion.img
            src={logoImg}
            alt="CÁMARA ARGENTINA DE EMPRESAS DE SALUD"
            className="hero-logo"
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          />
          <motion.p
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            “Comprometidos con representar y fortalecer a las empresas de salud de todo el país, construyendo un sector más federal, innovador y sostenible para todos sus asociados.”
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <Link to="/contacto" className="btn btn-primary">
              Contáctanos <FiArrowRight />
            </Link>
          </motion.div>
        </motion.div>
        <a href="#latest-news" className="scroll-down-indicator">
            <div className="scroll-wheel"></div>
        </a>
      </section>

      <main>
        {/* SECCIÓN DE ÚLTIMAS NOTICIAS */}
        {latestNews.length > 0 && (
          <motion.section
            id="latest-news"
            className="latest-news-section"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={containerVariants}
          >
            <SectionTitle
              title="Últimas Noticias"
              subtitle="Mantente al día con las novedades más recientes del sector de la salud."
            />
            <motion.div className="latest-news-grid" variants={containerVariants}>
              {latestNews.map((noticia) => (
                <motion.div
                  key={noticia.id}
                  className="news-card"
                  variants={itemVariants}
                  whileHover={{ y: -5, boxShadow: '0 10px 20px rgba(0, 51, 102, 0.08)' }}
                >
                  <Link to={`/noticia/${noticia.id}`} className="news-card-link">
                    <div className="news-card-image-container">
                        <img src={noticia.imagen} alt={noticia.titulo} className="news-card-image" />
                    </div>
                    <div className="news-card-content">
                      <span className="news-card-category">{noticia.categoria}</span>
                      <h4 className="news-card-title">{noticia.titulo}</h4>
                      <p className="news-card-excerpt">{noticia.resumen}</p>
                      <div className="news-card-meta">
                        <FiCalendar />
                        <span>{noticia.fechaPublicacion ? new Date(noticia.fechaPublicacion.seconds * 1000).toLocaleDateString('es-ES') : ''}</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
             <div className="see-all-news-container">
                <Link to="/noticias" className="btn btn-secondary">
                    Ver todas las noticias <FiArrowRight />
                </Link>
            </div>
          </motion.section>
        )}

        <AnimatedSeparator />

        {/* --- SECCIÓN PLAN ESTRATÉGICO --- */}
        <StrategicPlanSection />

        <AnimatedSeparator />
      
       {/* SECCIÓN RECURSOS */}
       <motion.section
          className="resources-section-styled"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
        >
            <div className="container">
                <SectionTitle
                    title="Recursos Exclusivos"
                    subtitle="Accede a nuestra biblioteca de conocimiento y herramientas para el sector."
                />
                <div className="resources-layout-grid">
                    <motion.div className="resources-content-col" variants={containerVariants}>
                        {resourcesData.map((resource) => (
                        <motion.div
                            key={resource.title}
                            className="resource-item"
                            variants={itemVariants}
                        >
                            <div className="resource-icon">{resource.icon}</div>
                            <div className="resource-text-content">
                              <Link to={resource.link} className="resource-link">
                                <h4>{resource.title}</h4>
                                <p>{resource.description}</p>
                              </Link>
                            </div>
                        </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </motion.section>


        
        <AnimatedSeparator />

        {/* SECCIÓN DE ORGANISMOS INTERNACIONALES */}  {/* SECCIÓN CORPORATIVA DE ALIANZAS */}
        <motion.section
          className="alliances-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
        >
          <div className="container">
            <div className="alliances-wrapper">
              <div className="strategic-partners-container">
                <h3 className="alliances-subtitle">Socios Estratégicos</h3>
                <motion.div className="main-partners-grid" variants={containerVariants}>
                  <motion.a href="https://www.confeclisa.com.ar/" target="_blank" rel="noopener noreferrer" className="main-partner-link" variants={itemVariants}>
                    <img src={confeclisaLogo} alt="Logo de Confeclisa" />
                  </motion.a>
                  <motion.a href="https://www.cenas.org.ar/" target="_blank" rel="noopener noreferrer" className="main-partner-link" variants={itemVariants}>
                    <img src={cenasLogo} alt="Logo de CENAS" />
                  </motion.a>
                </motion.div>
              </div>

              <div className="supporting-orgs-container">
                <h4 className="alliances-subtitle">Acompañan:</h4>
                <div className="supporting-orgs-grid">
                  {internationalOrganizations.map((org, index) => (
                    <a key={index} href={org.url} target="_blank" rel="noopener noreferrer" className="support-org-item" title={org.nombre}>
                      <img src={org.logo} alt={org.nombre} />
                    </a>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </motion.section>


      </main>
    </div>
  );
};

export default Home;